import { z } from 'zod';

import { NotificationChannel } from './enums.js';

/**
 * KANAL YETENEK TABLOSU — bildirim altyapısının çekirdeği.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 YENİ KANAL EKLEMEK = BURAYA BİR SATIR + BİR SÜRÜCÜ DOSYASI
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu tablo olmadan her kanal sorusu bir `switch` doğuruyordu ve o `switch`ler
 * altı ayrı dosyaya dağılıyordu: şablon doğrulayıcı "bu kanalda konu var mı"
 * diye soruyor, render "HTML mi düz metin mi" diye, tercih ekranı "bu kanal
 * kişiye mi kiracıya mı yazıyor" diye, gönderici "kaç karakter" diye. Altıncı
 * kanal eklendiğinde bunlardan birinin unutulması KESİNDİ — ve unutulan yer
 * derlemede değil, gönderim anında görünürdü.
 *
 * Şimdi hepsi tek kayıttan okuyor. `Record<NotificationChannel, …>` tipi,
 * enum'a bir değer eklendiğinde bu dosyayı DERLEME HATASI ile zorunlu kılıyor;
 * yani "kanalı ekledim ama yeteneklerini yazmadım" durumu mümkün değil.
 *
 * ---------------------------------------------------------------------------
 * Yeni kanal eklemenin tam listesi
 * ---------------------------------------------------------------------------
 *   1. `schema.prisma` → `NotificationChannel` enum'una değer + migration
 *   2. BU DOSYA        → yetenek satırı (derleyici zorluyor)
 *   3. `apps/api/src/notifications/channels/<kanal>.driver.ts` → sürücü
 *   4. `channel-registry.service.ts` → sürücüyü listeye ekle
 * Şablon ekranı, tercih ekranı, render, doğrulama ve gönderim defteri
 * KENDİLİĞİNDEN çalışır — hiçbirine dokunulmaz.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Yetenekler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kanalın adresi KİMDE.
 *
 * Bu alan "bildirim kime gidiyor" sorusunun teknik karşılığı ve gönderim
 * yolunu tümüyle değiştiriyor:
 *
 *   `user`      → adres `NotificationContact` tablosunda, KİŞİ başına.
 *                 Üç yöneticiye giden bir uyarı üç ayrı numaraya gider.
 *   `workspace` → adres `MessagingConnection`da, KİRACI başına. Slack
 *                 webhook'u bir ODAYA yazıyor; kişiye yazmıyor ve üç kez
 *                 göndermek aynı odaya üç mesaj demek olurdu.
 *   `internal`  → adres YOK. Uygulama içi bildirim; satır veritabanına
 *                 yazılıyor ve kullanıcı zaten kendi zilinde görüyor.
 */
export type ChannelAddressing = 'user' | 'workspace' | 'internal';

export interface NotificationChannelCapability {
  channel: NotificationChannel;

  /// Yönetici panelinde ve tercih ekranında görünen sabit ad. Çeviri
  /// ANAHTARI değil, marka adı: "WhatsApp" her dilde WhatsApp.
  label: string;

  addressing: ChannelAddressing;

  /**
   * Şablonda `bodyHtml` anlamlı mı.
   *
   * Yalnız e-posta. Slack ve WhatsApp kendi işaretleme dillerini kullanıyor
   * ama ikisi de HTML DEĞİL — `<b>` etiketi Slack'te harfi harfine görünür.
   * Bu yüzden onlar da `bodyText` üzerinden gidiyor.
   */
  supportsHtml: boolean;

  /// Şablonda `subject` anlamlı mı. SMS'in konusu yok; olsaydı gövdeye
  /// eklenip 160 karakterin bir kısmını yerdi.
  supportsSubject: boolean;

  /**
   * Gövdenin sığması gereken karakter sayısı. `null` = pratik bir sınır yok.
   *
   * 🔴 Bu bir UYARI eşiği değil, KESME sınırı. Aşan metin sağlayıcıya
   * gönderilmeden kırpılıyor: NetGSM 917 karakteri aşan isteği tümüyle
   * reddediyor ve "bildirim gitmedi" oluyordu; kırpılmış bir bildirim,
   * gönderilmemiş bildirimden iyi.
   */
  maxLength: number | null;

  /**
   * Gönderim için PLATFORM tarafında bir sağlayıcı yapılandırması gerekli mi
   * (`NotificationChannelConfig.configEnc`).
   *
   * `false` olanlar kendi bağlantılarını kiracıdan alıyor (Slack webhook'u,
   * kiracının webhook URL'i) ya da hiç bağlantı istemiyor (uygulama içi).
   */
  requiresProviderConfig: boolean;

  /**
   * Adres DOĞRULAMA gerektiriyor mu.
   *
   * 🔴 SMS ve Telegram'da `true` ve gerekçesi para: bildirim gövdesi kampanya
   * adı, günlük harcama ve bütçe taşıyor. Yanlış yazılmış bir numara bu veriyi
   * bir yabancıya gönderir ve SMS geri bildirim vermediği için bunu kimse fark
   * etmez. E-posta zaten kayıt sırasında doğrulanmış durumda.
   */
  requiresVerification: boolean;

  /**
   * Bu kanal kullanıcı tercihi ekranında görünüyor mu.
   *
   * `WEBHOOK` görünmüyor: kiracının kendi sistemine giden bir entegrasyon
   * akışı ve bunu bir yönetici kuruyor, tek tek kullanıcılar değil. Tercih
   * ekranında görünseydi bir üyenin kapatması entegrasyonu delerdi.
   */
  userSelectable: boolean;

  /// Gövdenin hangi işaretleme dilinde yazıldığı — düzenleyici ekranın
  /// hangi yardımı göstereceğini ve önizlemenin nasıl render edileceğini
  /// belirliyor.
  markup: 'html' | 'markdown' | 'slack-mrkdwn' | 'plain';
}

export const NOTIFICATION_CHANNEL_CAPABILITIES: Record<
  NotificationChannel,
  NotificationChannelCapability
> = {
  [NotificationChannel.IN_APP]: {
    channel: NotificationChannel.IN_APP,
    label: 'Uygulama içi',
    addressing: 'internal',
    supportsHtml: false,
    supportsSubject: true,
    // Zil menüsündeki kart iki satır gösteriyor; daha uzunu zaten kırpılıyor.
    maxLength: 500,
    requiresProviderConfig: false,
    requiresVerification: false,
    userSelectable: true,
    markup: 'plain',
  },

  [NotificationChannel.EMAIL]: {
    channel: NotificationChannel.EMAIL,
    label: 'E-posta',
    addressing: 'user',
    supportsHtml: true,
    supportsSubject: true,
    maxLength: null,
    // SMTP `.env`de ve `MailService` bildirim sisteminden ÖNCE ayakta olmak
    // zorunda (davet ve parola sıfırlama onu kullanıyor). Bu yüzden burada
    // sağlayıcı yapılandırması aranmıyor — kanal satırı yalnız aç/kapa.
    requiresProviderConfig: false,
    // Adres `User.email` ve kayıt akışında zaten doğrulanmış.
    requiresVerification: false,
    userSelectable: true,
    markup: 'html',
  },

  [NotificationChannel.SLACK]: {
    channel: NotificationChannel.SLACK,
    label: 'Slack',
    addressing: 'workspace',
    supportsHtml: false,
    supportsSubject: false,
    // Slack 40.000 karakter kabul ediyor ama 3.000'den uzun mesajı kırpıp
    // "show more" arkasına saklıyor — okunmayan kısım gönderilmemiş sayılır.
    maxLength: 3000,
    // Webhook KİRACIDA (`MessagingConnection.credentialEnc`), platformda değil.
    requiresProviderConfig: false,
    requiresVerification: false,
    userSelectable: true,
    markup: 'slack-mrkdwn',
  },

  [NotificationChannel.WHATSAPP]: {
    channel: NotificationChannel.WHATSAPP,
    label: 'WhatsApp',
    // 🔴 `user` — 9 Eyl 2026'da DEĞİŞTİ. Eskiden kiracı başına tek alıcı vardı
    // (`MessagingConnection.defaultDestination`) ve üç kişilik bir ekipte
    // ikisi bildirimi hiç görmüyordu. Artık adres kişiye ait; kiracı satırı
    // kişisel adresi olmayanlar için YEDEK olarak duruyor.
    addressing: 'user',
    supportsHtml: false,
    supportsSubject: false,
    maxLength: 4096,
    // Gönderen kurumsal numara PLATFORM tarafında
    // (`PlatformMessagingConnection`) — ama o tablo `MessagingService`in
    // kendi kaydı, `NotificationChannelConfig` değil. Bu yüzden `false`:
    // kanal kaydı yalnız aç/kapa ve sağlık için var.
    requiresProviderConfig: false,
    requiresVerification: true,
    userSelectable: true,
    markup: 'markdown',
  },

  [NotificationChannel.SMS]: {
    channel: NotificationChannel.SMS,
    label: 'SMS',
    addressing: 'user',
    supportsHtml: false,
    supportsSubject: false,
    /**
     * 🔴 640 — altı GSM parçası, bir tane değil.
     *
     * 160 karakter TEK parça; Türkçe karakter içeren metin GSM-03.38 dışına
     * çıkıp UCS-2'ye düşüyor ve parça 70 karaktere iniyor. "Bütçe uyarısı"
     * gibi bir cümle tek parçaya sığmıyor, yani 160'ta kesmek bildirimi
     * anlamsız bırakırdı. 640 üst sınır: sağlayıcı bunu kabul ediyor,
     * maliyeti öngörülebilir ve daha uzunu zaten kimsenin okumadığı bir SMS.
     */
    maxLength: 640,
    requiresProviderConfig: true,
    requiresVerification: true,
    userSelectable: true,
    markup: 'plain',
  },

  [NotificationChannel.TELEGRAM]: {
    channel: NotificationChannel.TELEGRAM,
    label: 'Telegram',
    addressing: 'user',
    supportsHtml: false,
    supportsSubject: false,
    // Bot API sınırı 4096; aşan istek 400 dönüyor.
    maxLength: 4096,
    // Bot jetonu PLATFORM tarafında: tek bot bütün kiracılara yazıyor.
    requiresProviderConfig: true,
    // 🔴 Doğrulama KODLA DEĞİL BAĞLANTIYLA (10 Eyl 2026): kullanıcı panelde
    // `t.me/<bot>?start=<tek kullanımlık jeton>` bağlantısını açıyor,
    // Telegram "Başlat"a basınca bota `/start <jeton>` gönderiyor ve sohbet
    // kimliği o mesajdan öğreniliyor. Kullanıcı sohbet kimliğini hiç görmüyor
    // — kodlu akışta önce onu bir yerden bulup yazması gerekiyordu ve bunun
    // bir yolu yoktu.
    requiresVerification: true,
    userSelectable: true,
    markup: 'markdown',
  },

  [NotificationChannel.WEBHOOK]: {
    channel: NotificationChannel.WEBHOOK,
    label: 'Webhook',
    addressing: 'workspace',
    supportsHtml: false,
    supportsSubject: true,
    maxLength: null,
    // Hedef URL ve imza sırrı KİRACIDA (`MessagingConnection`).
    requiresProviderConfig: false,
    requiresVerification: false,
    // Gerekçe `userSelectable` alanının başında.
    userSelectable: false,
    markup: 'plain',
  },
};

/// Sabit sıra — yönetici ve tercih ekranları bunu kullanıyor. `Object.keys`
/// sırası enum tanımına bağlı ve bir gün değişirse ekran sessizce karışır.
export const NOTIFICATION_CHANNEL_ORDER: NotificationChannel[] = [
  NotificationChannel.IN_APP,
  NotificationChannel.EMAIL,
  NotificationChannel.SMS,
  NotificationChannel.WHATSAPP,
  NotificationChannel.TELEGRAM,
  NotificationChannel.SLACK,
  NotificationChannel.WEBHOOK,
];

export function channelCapability(channel: NotificationChannel): NotificationChannelCapability {
  return NOTIFICATION_CHANNEL_CAPABILITIES[channel];
}

/// Kullanıcının tercih ekranında görebildiği kanallar.
export const USER_SELECTABLE_CHANNELS: NotificationChannel[] = NOTIFICATION_CHANNEL_ORDER.filter(
  (c) => NOTIFICATION_CHANNEL_CAPABILITIES[c].userSelectable,
);

/// Şablonu düzenlenebilen kanallar — hepsi. `IN_APP` dâhil: zil metnini de
/// yönetici değiştirebilmeli, aksi hâlde uygulama içi bildirim tek
/// özelleştirilemeyen yüzey olurdu.
export const TEMPLATED_CHANNELS: NotificationChannel[] = NOTIFICATION_CHANNEL_ORDER;

// ═══════════════════════════════════════════════════════════════════════════
// Sağlayıcı yapılandırması — kanal başına ŞEKİL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `NotificationChannelConfig.configEnc` içindeki JSON'un şekli.
 *
 * Kanal başına ayrı şema: yönetici formu bunlardan üretiliyor ve kaydetmeden
 * önce doğrulama buradan geçiyor. Tek bir `Record<string,string>` olsaydı
 * yanlış anahtar adıyla kaydedilen bir jeton ilk gönderime kadar sessiz
 * kalırdı.
 */

/// NetGSM — `https://api.netgsm.com.tr`.
///
/// `msgheader` NetGSM panelinden ONAYLANMIŞ gönderici başlığı. Onaysız başlık
/// isteği reddettiriyor (`30` hata kodu) ve bu, kimlik bilgisi hatasıyla
/// karışıyor — bu yüzden ayrı alan ve formda ayrı açıklama.
export const netgsmConfigSchema = z.object({
  provider: z.literal('netgsm'),
  /// NetGSM abone numarası (kullanıcı adı).
  userCode: z.string().min(3).max(40),
  password: z.string().min(1).max(80),
  /// Onaylı gönderici başlığı — "MENTORIVO" gibi.
  msgHeader: z.string().min(3).max(11),
  /// Ticari ileti mi (`appkey` gerektiren IYS akışı). Bildirimler HİZMET
  /// iletisi: kullanıcının kendi hesabına dair, izin gerektirmiyor.
  /// Alan yine de var çünkü NetGSM isteği tipe göre farklı uçlara yolluyor.
  commercial: z.boolean().default(false),
});
export type NetgsmConfig = z.infer<typeof netgsmConfigSchema>;

/// Telegram Bot API — tek bot, bütün kiracılar.
export const telegramConfigSchema = z.object({
  provider: z.literal('telegram'),
  /// `123456:ABC-DEF…` — BotFather'ın verdiği jeton.
  botToken: z.string().min(20).max(200),
  /// Bot kullanıcı adı (`mentorivo_bot`). Bağlantı düğmesinin adresi
  /// (`t.me/<ad>?start=…`) bundan kuruluyor; jetondan türetilebilir ama
  /// türetmek her ekran açılışında Telegram'a bir istek demekti.
  botUsername: z.string().min(3).max(64).optional(),
  /**
   * Gelen mesaj ucunun (`/notifications/telegram/webhook`) sırrı.
   *
   * 🔴 Formdan GELMİYOR, sunucu üretiyor: jeton her kaydedildiğinde yeni bir
   * sır üretilip `setWebhook`un `secret_token`ı olarak Telegram'a veriliyor;
   * Telegram her istekte onu `X-Telegram-Bot-Api-Secret-Token` başlığında
   * geri yolluyor. Sırsız bir uç, adresi bilen herkesin "şu sohbeti şu
   * kullanıcıya bağla" diyen sahte bir güncelleme yollaması demekti.
   * `CHANNEL_CONFIG_PUBLIC_FIELDS`te YOK — yönetici ekranına maskeli dönüyor.
   */
  webhookSecret: z
    .string()
    .min(32)
    .max(256)
    .regex(/^[A-Za-z0-9_-]+$/)
    .optional(),
});
export type TelegramConfig = z.infer<typeof telegramConfigSchema>;

/// Kanalın sağlayıcı yapılandırması yoksa (`requiresProviderConfig: false`)
/// bu şekil kaydediliyor — kayıt yine de var çünkü `enabled` bayrağı orada.
export const emptyChannelConfigSchema = z.object({ provider: z.literal('none') });

export const channelProviderConfigSchema = z.discriminatedUnion('provider', [
  netgsmConfigSchema,
  telegramConfigSchema,
  emptyChannelConfigSchema,
]);
export type ChannelProviderConfig = z.infer<typeof channelProviderConfigSchema>;

/**
 * Hangi kanalda hangi sağlayıcılar seçilebilir.
 *
 * Bugün her kanalın tek sağlayıcısı var; liste yine de bir dizi çünkü SMS'in
 * ikinci bir sağlayıcısı (Twilio, İleti Merkezi) eklendiğinde form
 * kendiliğinden bir seçici göstersin ve kod değişmesin.
 */
export const CHANNEL_PROVIDERS: Partial<Record<NotificationChannel, readonly string[]>> = {
  [NotificationChannel.SMS]: ['netgsm'],
  [NotificationChannel.TELEGRAM]: ['telegram'],
};

/// Sırların yönetici ekranına GİTMEYEN alanları — yanıt kurulurken bunlar
/// maskeleniyor. Alanı burada saymayı unutmak sırrı sızdırır, bu yüzden
/// maskeleme `maskChannelConfig` içinde İZİN LİSTESİYLE çalışıyor: sayılmayan
/// her alan maskeleniyor, tersi değil.
export const CHANNEL_CONFIG_PUBLIC_FIELDS: readonly string[] = [
  'provider',
  'userCode',
  'msgHeader',
  'commercial',
  'botUsername',
];

/**
 * Yönetici ekranına dönen güvenli hâl.
 *
 * 🔴 İZİN LİSTESİ, yasak listesi DEĞİL. "`password` ve `botToken`ı sil"
 * deseydik, üçüncü sağlayıcının `apiSecret` alanı eklendiğinde onu silmeyi
 * hatırlamak gerekirdi — ve hatırlanmadığında sır ekrana düşerdi. Burada
 * tersine: listede olmayan her alan maskeleniyor, yeni alan varsayılan
 * olarak GİZLİ.
 */
export function maskChannelConfig(
  config: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!config) return null;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config)) {
    if (CHANNEL_CONFIG_PUBLIC_FIELDS.includes(key)) {
      out[key] = value;
      continue;
    }
    // Değerin VARLIĞI bilgi: form "kayıtlı bir parola var, değiştirmek için
    // yaz" diyebilsin diye boş dize yerine sabit bir işaret dönüyor.
    out[key] = value === null || value === undefined || value === '' ? null : '••••••••';
  }
  return out;
}

/**
 * Maskeli hedef — `NotificationDelivery.destination` kolonuna yazılan şey.
 *
 * Destek ekranı bu tabloyu okuyor ve orada tam telefon numarası görünmemeli.
 * Maskeli hâl "doğru numaraya mı gitti" sorusunu zaten cevaplıyor: kullanıcı
 * kendi numarasının son dört hanesini tanır.
 */
export function maskDestination(
  channel: NotificationChannel,
  address: string | null | undefined,
): string | null {
  if (!address) return null;
  const value = address.trim();
  if (!value) return null;

  if (channel === NotificationChannel.EMAIL) {
    const at = value.indexOf('@');
    // `@` yoksa adres zaten bozuk; e-posta gibi maskelemek yanıltıcı olurdu.
    if (at <= 0) return maskTail(value);
    const name = value.slice(0, at);
    const domain = value.slice(at);
    const head = name.slice(0, 1);
    return `${head}${'*'.repeat(Math.max(1, Math.min(6, name.length - 1)))}${domain}`;
  }

  if (channel === NotificationChannel.WEBHOOK || channel === NotificationChannel.SLACK) {
    // URL'de sır YOL üzerinde taşınıyor (Slack webhook'unun tamamı bir sır).
    // Sadece kökü göstermek yeterli ve güvenli.
    try {
      const url = new URL(value);
      return `${url.protocol}//${url.host}/…`;
    } catch {
      return maskTail(value);
    }
  }

  return maskTail(value);
}

/// Son dört karakter açık, gerisi yıldız. Telefon ve sohbet kimliği için.
function maskTail(value: string): string {
  if (value.length <= 4) return '*'.repeat(value.length);
  const tail = value.slice(-4);
  const headLength = Math.min(4, value.length - 4);
  return `${value.slice(0, headLength)}${'*'.repeat(Math.max(3, value.length - 4 - headLength))}${tail}`;
}
